import { EventBus } from '@hcie/shared';

/**
 * @interface FileEntry
 * @description Structure of a file entry from the OS
 */
export interface FileEntry {
    name: string;
    path: string;
    is_dir: boolean;
}

/**
 * @class PlatformService
 * @description Provides a unified API for Platform-specific operations (IO, Shell, OS Info).
 * Automatically switches between Tauri (Desktop) and standard Web APIs.
 */
export class PlatformService {
    private static cachedOs: string | null = null;

    /**
     * Detects if running inside Tauri
     */
    public static isTauri(): boolean {
        return !!(window as any).__TAURI__;
    }

    /**
     * Detects if running on a mobile device
     */
    public static isMobile(): boolean {
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    }

    /**
     * Gets OS information (e.g., 'linux', 'windows', 'macos', 'web')
     */
    public static async getOS(): Promise<string> {
        if (this.cachedOs) return this.cachedOs;

        if (this.isTauri()) {
            try {
                const tauri = (window as any).__TAURI__;
                this.cachedOs = await tauri.core.invoke('cmd_get_os_info');
            } catch (e) {
                console.error('[Platform] Failed to get OS via Tauri:', e);
                this.cachedOs = 'desktop-unknown';
            }
        } else {
            this.cachedOs = 'web';
        }
        return this.cachedOs || 'web';
    }

    /**
     * Reads a file as an ArrayBuffer
     */
    public static async readFile(path: string): Promise<ArrayBuffer> {
        if (this.isTauri()) {
            const tauri = (window as any).__TAURI__;
            const bytes = await tauri.core.invoke('cmd_read_file', { path });
            return new Uint8Array(bytes).buffer;
        } else {
            // Web fallback: fetch if it's a URL or simulate if possible
            const response = await fetch(path);
            return await response.arrayBuffer();
        }
    }

    /**
     * Scans a directory for entries
     */
    public static async readDir(path: string): Promise<FileEntry[]> {
        if (this.isTauri()) {
            const tauri = (window as any).__TAURI__;
            return await tauri.core.invoke('cmd_read_dir', { path });
        } else {
            console.warn('[Platform] readDir is not supported on web.');
            return [];
        }
    }

    /**
     * Opens a native file picker
     */
    public static async openFilePicker(options: { multiple?: boolean; directory?: boolean; filters?: any[] } = {}): Promise<string | string[] | null> {
        if (this.isTauri()) {
            const tauri = (window as any).__TAURI__;
            return await tauri.dialog.open(options);
        } else {
            // Web Fallback: <input type="file">
            return new Promise((resolve) => {
                const input = document.createElement('input');
                input.type = 'file';
                input.multiple = !!options.multiple;
                if (options.filters) {
                    input.accept = options.filters.map(f => f.extensions.map(e => `.${e}`).join(',')).join(',');
                }
                
                input.onchange = () => {
                    if (input.files) {
                        const files = Array.from(input.files);
                        // On web we cannot get real paths, so we might need to handle Blobs differently
                        // This bridge might need to emit the blobs instead of paths
                        EventBus.emit('WEB_FILES_PICKED', { files });
                        resolve(null); // Web handles via event
                    } else {
                        resolve(null);
                    }
                };
                input.click();
            });
        }
    }

    /**
     * Resizes an image from a local file path using Rust (Tauri) or Canvas 2D fallback (web).
     * Returns a PNG-encoded ArrayBuffer.
     */
    public static async resizeImage(
        sourcePath: string,
        width: number,
        height: number,
        filter: 'lanczos3' | 'catmull_rom' | 'bilinear' | 'nearest' | 'gaussian' = 'lanczos3'
    ): Promise<ArrayBuffer> {
        if (this.isTauri()) {
            const tauri = (window as any).__TAURI__;
            const bytes: number[] = await tauri.core.invoke('cmd_resize_image', {
                path: sourcePath,
                width,
                height,
                filter,
            });
            return new Uint8Array(bytes).buffer;
        } else {
            return this._resizeWithCanvas(sourcePath, width, height);
        }
    }

    private static _resizeWithCanvas(src: string, width: number, height: number): Promise<ArrayBuffer> {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d')!;
                ctx.drawImage(img, 0, 0, width, height);
                canvas.toBlob((blob) => {
                    if (!blob) return reject(new Error('Canvas toBlob failed'));
                    blob.arrayBuffer().then(resolve).catch(reject);
                }, 'image/png');
            };
            img.onerror = reject;
            img.src = src;
        });
    }

    /**
     * Converts a local file path to a URL usable in <img> tags.
     * Priority: __TAURI_INTERNALS__.convertFileSrc → __TAURI__.core.convertFileSrc → asset:// manual URL
     */
    public static convertFileSrc(path: string): string {
        if (this.isTauri()) {
            // Tauri v2 injects __TAURI_INTERNALS__ — most reliable source
            const internals = (window as any).__TAURI_INTERNALS__;
            if (internals?.convertFileSrc) {
                return internals.convertFileSrc(path);
            }
            // Older Tauri v2 builds expose it via __TAURI__.core
            const tauri = (window as any).__TAURI__;
            if (tauri?.core?.convertFileSrc) {
                return tauri.core.convertFileSrc(path);
            }
            // Manual fallback: asset://localhost/<absolute-path>
            // On Windows: C:\foo\bar → asset://localhost/C:/foo/bar
            const normalized = path.replace(/\\/g, '/');
            const withSlash = normalized.startsWith('/') ? normalized : `/${normalized}`;
            return `asset://localhost${withSlash}`;
        }
        return path;
    }

    /**
     * Async version: reads file via Rust and returns a blob: URL.
     * Use this when the asset protocol is unavailable or misconfigured.
     * IMPORTANT: call URL.revokeObjectURL(url) when the image is no longer needed.
     */
    public static async convertFileSrcAsync(path: string): Promise<string> {
        if (this.isTauri()) {
            try {
                const buffer = await this.readFile(path);
                const ext = path.split('.').pop()?.toLowerCase() || 'jpeg';
                const mimeMap: Record<string, string> = {
                    png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg',
                    webp: 'image/webp', gif: 'image/gif', bmp: 'image/bmp',
                };
                const mime = mimeMap[ext] || 'image/jpeg';
                const blob = new Blob([buffer], { type: mime });
                return URL.createObjectURL(blob);
            } catch {
                return this.convertFileSrc(path);
            }
        }
        return path;
    }
}
